import { PUBLIC_BEARER_TOKEN_PAGE_PREFIXES } from "@/lib/seo/bearer-token-pages";

/**
 * Public bearer-token surfaces where third-party analytics must NOT load.
 *
 * `/portal/<token>`, `/sign/<token>`, `/share/<token>`, `/shared/<token>`,
 * `/crew/<token>`, `/site/<token>`, `/equipment/<token>` and
 * `/team/join/<token>` carry a secret token in the URL path. Google Analytics
 * (`gtag('config')` sends
 * `page_location`) and Microsoft Clarity (session-records the URL) would ship
 * that full URL — including the token — to Google/Microsoft the moment a
 * visitor accepts the cookie banner. The portal `magicToken` is the sole
 * credential for check-in, sign-off, and document PDFs — a manager can now
 * rotate it (#1887), but a copy shipped to an analytics vendor still sits
 * outside that control, so keep it suppressed.
 *
 * The analytics components short-circuit (render nothing, effects no-op) on
 * these prefixes.
 */
export const ANALYTICS_TOKEN_PATH_PREFIXES = [
  ...PUBLIC_BEARER_TOKEN_PAGE_PREFIXES,
  // Public API equivalents whose dynamic segment is the same bearer token.
  "/api/share/",
  "/api/shared/",
  "/api/sign/",
  "/api/portal/",
  "/api/crew/",
  "/api/site-entrance/",
  // Anonymous funnel routes bind this session credential to a cookie too, but
  // it remains secret URL material and must not reach telemetry or storage.
  "/api/draft-projects/",
] as const;

/** Credential-bearing query/fragment fields used by public API links. */
export const ANALYTICS_BEARER_PARAM_KEYS = ["token", "api_key"] as const;

/**
 * Query params holding USER-ENTERED FREE TEXT. Not credentials — but on this
 * product a search term is a supplier name, an operator name, a serial number
 * or a registration plate, and `landingPage`/`sourcePath` are forwarded to
 * Clerk metadata, the signup Slack alert and the attribution DB.
 *
 * These are matched at every nesting depth, which is the point: a signed-out
 * visitor opening an authenticated URL is bounced to
 * `/login?redirect_url=%2Fplant%3Fsearch%3D<term>` before any page-level strip
 * can run, so the term arrives one level down inside the redirect target.
 *
 * Only a NON-EMPTY value suppresses — a bare `?search=` carries nothing — so a
 * normal `/login?redirect_url=%2Fprojects` keeps its attribution intact and the
 * marketing data is only lost on the visits that actually carry free text.
 *
 * `q` and `query` are deliberately NOT here. The same predicate scrubs the
 * REFERRER, and `q` is the organic keyword on every search engine — including
 * it threw away `https://google.com/search?q=…`, which is attribution the
 * business actually wants. This set is our own param names; a new surface with
 * a free-text filter adds its param here (and the conformance test pins it).
 *
 * `email` and `phone` are the exception to "our own param names": no page uses
 * them today, but they are unambiguous direct-PII shapes (a prefill link, a
 * future contact form), and — unlike `q` — a referrer carrying `?email=` or
 * `?phone=` holds no marketing signal worth keeping, so pre-adding them costs
 * nothing and closes the hole before a page opens it (#1988).
 */
export const ANALYTICS_FREE_TEXT_PARAM_KEYS = [
  "search",
  "email",
  "phone",
] as const;

/**
 * Query params that carry a post-auth redirect target. A signed-out visitor
 * hitting a token page is bounced to `/login?redirect_url=%2Fsite%2F<token>`
 * (or `/signup?redirect_url=%2Fteam%2Fjoin%2F<token>`), so the token rides in
 * the query string on `/login`/`/signup` where the pathname alone looks safe.
 *
 * MUST include every redirect param the auth flow forwards — Clerk's
 * `*_force_redirect_url` / `*_fallback_redirect_url` / `after_sign_*_url` are
 * all honoured (see `components/auth/attributed-sign-up.tsx`), so a token in any
 * of them must suppress analytics too. All keys are lower-case (compared after
 * `.toLowerCase()`).
 */
export const ANALYTICS_REDIRECT_PARAM_KEYS = [
  "redirect_url",
  "redirect_uri",
  "redirect",
  "next",
  "returnto",
  "return_to",
  "callbackurl",
  // Clerk redirect controls (sign-up + sign-in variants).
  "after_sign_up_url",
  "after_sign_in_url",
  "sign_up_force_redirect_url",
  "sign_up_fallback_redirect_url",
  "sign_in_force_redirect_url",
  "sign_in_fallback_redirect_url",
] as const;

export const ANALYTICS_LOCATION_CHANGE_EVENT = "tsb:analytics-location-change";

const REDIRECT_PARAM_KEYS = new Set<string>(ANALYTICS_REDIRECT_PARAM_KEYS);
const BEARER_PARAM_KEYS = new Set<string>(ANALYTICS_BEARER_PARAM_KEYS);
const FREE_TEXT_PARAM_KEYS = new Set<string>(ANALYTICS_FREE_TEXT_PARAM_KEYS);

/**
 * Auth pages a signed-out visitor is bounced to WITH the token in the query
 * (`/login?redirect_url=%2Fsite%2F<token>`, `/signup?redirect_url=…`). These
 * client components can't see the query string during SSR, so on these paths
 * the suppression must fail CLOSED on the first (server + hydration) render —
 * the precise query-aware client check then re-enables analytics after
 * hydration when the query is actually token-free.
 *
 * Clerk renders sign-in/up as OPTIONAL CATCH-ALL routes
 * (`login/[[...login]]`, `signup/[[...signup]]`), so the verification / SSO /
 * continue sub-paths (`/signup/verify-email-address`, `/login/factor-one`, …)
 * forward the same redirect query and must fail closed too — hence the whole
 * `/login/*` and `/signup/*` subtree, not just the exact base path.
 */
const AUTH_REDIRECT_LANDING_BASES = ["/login", "/signup"] as const;

// A base is required for ordinary relative paths, but absolute-looking values
// must be parsed WITHOUT it first. WHATWG URL parsing canonicalises inputs such
// as `https:example.com/share/token`, `https:/example.com/share/token`, and
// backslash variants into live HTTPS URLs. Parsing those values only after a
// `^https?://` check treats them as harmless relative text even though the next
// consumer (a browser, `new URL`, or an HTTP client) resolves the credential.
const URL_STRING_PARSE_BASE = "https://url-safety.invalid/";
export const ANALYTICS_MAX_NESTED_REDIRECT_DEPTH = 5;
export const ANALYTICS_MAX_URL_DECODE_PASSES = 5;

/**
 * Parse a URL string that may be absolute, scheme-relative (`//host/x`) or
 * path-relative (`/x?y=z`), resolving the last two against a sentinel base.
 *
 * Exported for other credential guards to reuse. Reimplementing this is how a
 * guard ends up accepting `//alice:hunter2@host/` or
 * `?url=%2Fcallback%3Faccess_token%3Dsecret`, because a bare `new URL(value)`
 * throws on both and a caller reads the throw as "not a URL, nothing to see".
 * The sentinel host is deliberately unresolvable — callers must use the result
 * to INSPECT a value, never to decide where to connect.
 */
export function parseUrlStringWithBase(value: string): URL | null {
  return parsedHttpUrl(value);
}

/**
 * Percent-decode ASCII escapes, repeatedly, within a bounded number of passes.
 *
 * Exported alongside the parser above because the two belong together. Returns
 * `exhausted: true` when the value was STILL decoding when the budget ran out,
 * which every caller must treat as fail-closed. Decoding byte-by-byte rather
 * than with `decodeURIComponent` is the point: one unrelated malformed `%`
 * must not stop a valid `%3Faccess_token%3D…` later in the same
 * attacker-controlled value from being inspected.
 */
export function decodeUrlAscii(value: string): {
  value: string;
  exhausted: boolean;
} {
  return decodeUrlAsciiBounded(value);
}

function parsedHttpUrl(value: string): URL | null {
  try {
    const absolute = new URL(value);
    if (absolute.protocol === "http:" || absolute.protocol === "https:") {
      return absolute;
    }
    return null;
  } catch {
    try {
      const relative = new URL(value, URL_STRING_PARSE_BASE);
      return relative.protocol === "http:" || relative.protocol === "https:"
        ? relative
        : null;
    } catch {
      return null;
    }
  }
}

function decodeUrlAsciiPass(value: string): string {
  // Token routes and redirect syntax are ASCII. Decode individual ASCII bytes
  // instead of using decodeURIComponent's all-or-nothing UTF-8 parser: one
  // unrelated malformed `%` must not prevent a valid `%2Fshare%2Ftoken` later
  // in the same attacker-controlled value from being inspected.
  return value.replace(/%([0-9a-f]{2})/gi, (match, hex: string) => {
    const byte = Number.parseInt(hex, 16);
    return byte <= 0x7f ? String.fromCharCode(byte) : match;
  });
}

function decodeUrlAsciiBounded(value: string): {
  value: string;
  exhausted: boolean;
} {
  let decoded = value;
  for (let pass = 0; pass < ANALYTICS_MAX_URL_DECODE_PASSES; pass += 1) {
    const next = decodeUrlAsciiPass(decoded);
    if (next === decoded) return { value: decoded, exhausted: false };
    decoded = next;
  }
  return {
    value: decoded,
    exhausted: decodeUrlAsciiPass(decoded) !== decoded,
  };
}

function canonicalSuppressionPath(pathname: string): string | null {
  const decoded = decodeUrlAsciiBounded(pathname);
  if (decoded.exhausted) return null;
  // `pathname` has already been split from `search` by the first URL parse.
  // Repeated decoding can reveal a delimiter that was encoded during that
  // split (`/login%3Fredirect_url=...`). Reparsing and returning only pathname
  // would discard the newly visible redirect query. Treat either delimiter as
  // unproven and fail closed; these are pathological path spellings, while a
  // false negative would expose a bearer token.
  if (decoded.value.includes("?") || decoded.value.includes("#")) return null;
  const normalized = decoded.value.replace(/\\/g, "/").replace(/\/{2,}/g, "/");
  try {
    return new URL(normalized, URL_STRING_PARSE_BASE).pathname;
  } catch {
    return normalized;
  }
}

export function isAnalyticsSuppressedPath(pathname: string | null): boolean {
  if (!pathname) return false;
  const canonicalPath = canonicalSuppressionPath(pathname);
  if (canonicalPath === null) return true;
  return ANALYTICS_TOKEN_PATH_PREFIXES.some((prefix) =>
    canonicalPath.startsWith(prefix),
  );
}

/**
 * The SSR / first-render suppression decision, made WITHOUT the query string.
 * Suppresses token paths outright, and fails closed on the auth redirect
 * landing pages (whose query may carry a token). Everything else renders
 * analytics normally, so ordinary pages keep their SSR-emitted tag.
 */
export function isAnalyticsSuppressedServerPath(
  pathname: string | null,
): boolean {
  if (!pathname) return false;
  const canonicalPath = canonicalSuppressionPath(pathname);
  if (canonicalPath === null) return true;
  if (isAnalyticsSuppressedPath(canonicalPath)) return true;
  return AUTH_REDIRECT_LANDING_BASES.some(
    (base) => canonicalPath === base || canonicalPath.startsWith(`${base}/`),
  );
}

/**
 * Path-based suppression PLUS a check of token-bearing redirect query values,
 * so a token that rides in `?redirect_url=` on `/login`/`/signup` is caught
 * too. `search` is a raw query string (e.g. `window.location.search`).
 */
/**
 * Whether a URL STRING (relative `/x?y=z` or absolute `https://host/x?y=z`) is
 * a token-bearing surface. Used to scrub captured attribution values —
 * `landingPage`, `sourcePath`, and `document.referrer` — that would otherwise
 * forward the token to Clerk / Slack / the attribution DB.
 */
export function isSuppressedUrlString(
  value: string | null | undefined,
): boolean {
  if (!value) return false;
  const parsed = parsedHttpUrl(value);
  if (!parsed) return false;
  return isAnalyticsSuppressedUrl(parsed.pathname, parsed.search, parsed.hash);
}

export function isAnalyticsSuppressedUrl(
  pathname: string | null,
  search: string | null | undefined,
  hash?: string | null,
): boolean {
  const ancestors = new Set<string>();
  return (
    isAnalyticsSuppressedUrlAtDepth(pathname, search, 0, ancestors) ||
    isAnalyticsSuppressedFragmentAtDepth(hash, 0, ancestors)
  );
}

function isAnalyticsSuppressedFragmentAtDepth(
  hash: string | null | undefined,
  depth: number,
  ancestors: ReadonlySet<string>,
): boolean {
  if (!hash) return false;
  const decoded = decodeUrlAsciiBounded(
    hash.startsWith("#") ? hash.slice(1) : hash,
  );
  if (decoded.exhausted) return true;
  const fragment = decoded.value;
  if (!fragment) return false;
  const state = `fragment\u0000${fragment}`;
  if (ancestors.has(state)) return true;
  const nextAncestors = new Set(ancestors).add(state);
  const fragmentParams = new URLSearchParams(
    fragment.startsWith("?") ? fragment.slice(1) : fragment,
  );
  // Bare-param fragments (`#search=term`, `#token=…`) must be checked HERE:
  // the reparse below turns a bare fragment into a pathname (`/search=term`),
  // so the URL-depth param checks never see these keys. Bearer keys always
  // suppress; free-text keys follow the same non-empty rule as query params.
  for (const [key, value] of fragmentParams) {
    const lower = key.toLowerCase();
    if (BEARER_PARAM_KEYS.has(lower)) return true;
    if (FREE_TEXT_PARAM_KEYS.has(lower) && value !== "") return true;
  }
  const carriesRedirect = [...fragmentParams.keys()].some((key) =>
    REDIRECT_PARAM_KEYS.has(key.toLowerCase()),
  );
  const fragmentUrl = parsedHttpUrl(
    fragment.startsWith("?") || carriesRedirect
      ? `/${fragment.startsWith("?") ? "" : "?"}${fragment}`
      : fragment,
  );
  if (!fragmentUrl) return true;
  if (
    isAnalyticsSuppressedUrlAtDepth(
      fragmentUrl.pathname,
      fragmentUrl.search,
      depth,
      nextAncestors,
    )
  ) {
    return true;
  }
  if (!fragmentUrl.hash) return false;
  if (depth >= ANALYTICS_MAX_NESTED_REDIRECT_DEPTH) return true;
  return isAnalyticsSuppressedFragmentAtDepth(
    fragmentUrl.hash,
    depth + 1,
    nextAncestors,
  );
}

function isAnalyticsSuppressedUrlAtDepth(
  pathname: string | null,
  search: string | null | undefined,
  depth: number,
  ancestors: ReadonlySet<string>,
): boolean {
  if (isAnalyticsSuppressedPath(pathname)) return true;
  if (!search) return false;
  const state = `${pathname ?? ""}\u0000${search}`;
  // A redirect cycle or an attacker-controlled chain beyond the inspection
  // budget cannot be proven token-free. Fail closed: losing analytics on a
  // pathological auth URL is harmless; exporting its embedded credential is
  // not. Each branch gets its own ancestry set so repeated safe siblings do
  // not look like a cycle.
  if (ancestors.has(state)) return true;
  const nextAncestors = new Set(ancestors).add(state);
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search);
  } catch {
    return false;
  }
  for (const [key, value] of params) {
    if (BEARER_PARAM_KEYS.has(key.toLowerCase())) return true;
    if (FREE_TEXT_PARAM_KEYS.has(key.toLowerCase()) && value !== "")
      return true;
    if (!REDIRECT_PARAM_KEYS.has(key.toLowerCase())) continue;
    // URLSearchParams already percent-decodes the value. It may be a bare path
    // ("/site/<token>") or any WHATWG-canonicalisable HTTP URL — reduce to the
    // parsed pathname either way so slash variants cannot bypass the guard.
    const decodedTarget = decodeUrlAsciiBounded(value);
    if (decodedTarget.exhausted) return true;
    const candidate = parsedHttpUrl(decodedTarget.value);
    const candidatePath = candidate?.pathname ?? decodedTarget.value;
    if (isAnalyticsSuppressedPath(candidatePath)) return true;
    if (candidate?.hash) {
      if (depth >= ANALYTICS_MAX_NESTED_REDIRECT_DEPTH) return true;
      if (
        isAnalyticsSuppressedFragmentAtDepth(
          candidate.hash,
          depth + 1,
          nextAncestors,
        )
      ) {
        return true;
      }
    }
    if (!candidate?.search) continue;
    if (depth >= ANALYTICS_MAX_NESTED_REDIRECT_DEPTH) return true;
    if (
      isAnalyticsSuppressedUrlAtDepth(
        candidatePath,
        candidate.search,
        depth + 1,
        nextAncestors,
      )
    ) {
      return true;
    }
  }
  return false;
}
